/**
 * Changing the address you sign in with. The change is asked for here and
 * confirmed from the new mailbox (EmailChangeConfirmPage); until then the
 * member record carries `pendingEmail`, which is what the Settings page
 * shows. An organization on single sign-on gets no editor at all: the
 * address belongs to its identity provider.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { app } from '../../lib/firebase';
import { call } from '../../lib/callable';
import { resolveEmailOrg } from '../../auth/sso';

const db = getFirestore(app);

export interface PendingEmail {
  email: string;
  /** ISO date-time the confirmation link stops working. */
  expiresAt: string | null;
}

function toIso(value: unknown): string | null {
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    const d = (value as { toDate: () => Date }).toDate();
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  return typeof value === 'string' ? value : null;
}

export function usePendingEmail(orgId: string | null, uid: string | null) {
  return useQuery({
    queryKey: ['pending-email', orgId, uid],
    enabled: Boolean(orgId && uid),
    queryFn: async (): Promise<PendingEmail | null> => {
      const snap = await getDoc(doc(db, 'orgs', orgId!, 'members', uid!));
      const p = snap.data()?.pendingEmail as { email?: unknown; expiresAt?: unknown } | undefined;
      if (!p || typeof p.email !== 'string') return null;
      const expiresAt = toIso(p.expiresAt);
      if (expiresAt && new Date(expiresAt).getTime() < Date.now()) return null;
      return { email: p.email, expiresAt };
    },
  });
}

/** Whether this address signs in through an identity provider, which owns it. */
export function useAddressManagedBySso(email: string | null) {
  return useQuery({
    queryKey: ['address-sso', email],
    enabled: Boolean(email),
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      const org = await resolveEmailOrg(email!);
      return org.ssoEnabled === true;
    },
  });
}

export interface EmailChangeResult {
  newEmail: string;
  emailSent: boolean;
  expiresAt: string;
}

export function useRequestEmailChange(orgId: string | null, uid: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { newEmail: string }) => call<{ newEmail: string }, EmailChangeResult>('requestEmailChange', input),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['pending-email', orgId, uid] }); },
  });
}

export function useCancelEmailChange(orgId: string | null, uid: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => call<Record<string, never>, { success: boolean }>('cancelEmailChange', {}),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['pending-email', orgId, uid] }); },
  });
}

/** The link from the new mailbox. Signing in is not required. */
export function confirmEmailChange(token: string): Promise<{ email: string }> {
  return call<{ token: string }, { email: string }>('confirmEmailChange', { token });
}
